import { formatInTimeZone, toDate } from "date-fns-tz";

/**
 * Gets the local timezone identifier (e.g., "America/Sao_Paulo", "America/New_York").
 * This is used for DST-aware date operations.
 */
function getLocalTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Whitelist pattern for valid Instagram handle characters.
 * Allows: lowercase letters (a-z), numbers (0-9), underscore (_), and period (.)
 */
const INSTAGRAM_HANDLE_WHITELIST_PATTERN = /^[a-z0-9_.]+$/;

/**
 * Pattern to match and remove control characters from strings.
 * Control characters are ASCII 0-31 (including null, bell, backspace, etc.)
 * and ASCII 127 (DEL). These are non-printable characters that should
 * never appear in a valid Instagram handle.
 */
const CONTROL_CHARACTER_PATTERN = /[\x00-\x1f\x7f]/g;

/**
 * Maximum length for Instagram handles (per Instagram's limit).
 */
export const INSTAGRAM_HANDLE_MAX_LENGTH = 30;

/**
 * Extracts an Instagram handle from a pasted URL.
 * Supports variations: https/http, www/m subdomain, trailing slash, query params.
 *
 * @param input - The raw user input
 * @returns The extracted handle, or null if the input is not an Instagram URL
 */
export function extractHandleFromInstagramUrl(input: string): string | null {
  const trimmed = input.trim();
  const match = trimmed.match(
    /^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/([a-zA-Z0-9_.]+)\/?(?:\?.*)?$/
  );
  return match?.[1] ?? null;
}

/**
 * Normalizes an Instagram handle by:
 * - Extracting handle from Instagram URL if pasted
 * - Applying NFKC Unicode normalization (canonical composition)
 * - Converting to lowercase
 * - Stripping control characters
 * - Trimming whitespace
 * - Removing @ prefix if present
 *
 * NFKC normalization ensures consistent representation of Unicode characters,
 * converting compatibility characters to their canonical forms. This prevents
 * spoofing attacks using visually similar Unicode characters.
 *
 * Control character stripping removes non-printable ASCII characters (0x00-0x1F and 0x7F)
 * that could potentially bypass validation or cause issues in processing.
 *
 * @param handle - The Instagram handle to normalize
 * @returns The normalized handle
 */
export function normalizeInstagramHandle(handle: string): string {
  const fromUrl = extractHandleFromInstagramUrl(handle);
  if (fromUrl) {
    return fromUrl.toLowerCase();
  }

  return handle
    .normalize("NFKC")
    .toLowerCase()
    .replace(CONTROL_CHARACTER_PATTERN, "")
    .trim()
    .replace(/^@/, "");
}

/**
 * Checks if an Instagram handle becomes empty after normalization.
 * This is useful for providing specific error feedback when users enter
 * invalid handles like only whitespace, @ symbol, or control characters.
 *
 * @param handle - The Instagram handle to check
 * @returns true if the handle becomes empty after normalization, false otherwise
 */
export function isEmptyAfterNormalization(handle: string): boolean {
  return normalizeInstagramHandle(handle).length === 0;
}

/**
 * Validates if a string is a valid Instagram handle format.
 * A valid handle must:
 * - Have at least 1 character after normalization
 * - Have at most 30 characters after normalization (Instagram's limit)
 * - Contain only whitelisted characters (a-z, 0-9, _, .)
 *
 * @param handle - The string to validate
 * @returns true if the normalized handle is valid, false otherwise
 */
export function isValidInstagramHandle(handle: string): boolean {
  const normalized = normalizeInstagramHandle(handle);
  return (
    normalized.length >= 1 &&
    normalized.length <= INSTAGRAM_HANDLE_MAX_LENGTH &&
    INSTAGRAM_HANDLE_WHITELIST_PATTERN.test(normalized)
  );
}

/**
 * Returns the current date in YYYY-MM-DD format using the server's local timezone.
 * This is crucial for daily aggregations, digests, and statistics that need to
 * align with the calendar date where the server is running.
 *
 * Uses date-fns-tz with the local timezone for DST-aware date formatting.
 * This ensures correct date handling during DST transitions.
 *
 * @returns Current date in YYYY-MM-DD format
 */
export function getLocalDateString(): string {
  return formatInTimeZone(new Date(), getLocalTimeZone(), "yyyy-MM-dd");
}

/**
 * Converts a Unix timestamp to a date string in YYYY-MM-DD format
 * using the server's local timezone.
 *
 * Uses date-fns-tz for DST-aware conversion that correctly handles
 * timestamps during DST transition periods.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Date string in YYYY-MM-DD format
 */
export function timestampToLocalDateString(timestamp: number): string {
  return formatInTimeZone(toDate(timestamp), getLocalTimeZone(), "yyyy-MM-dd");
}

/**
 * Truncates text to a specified maximum length.
 * If the text is longer than maxLength, it adds an ellipsis (...) at the end.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length of the resulting string (including ellipsis)
 * @returns The truncated text with ellipsis if it exceeded maxLength, otherwise the original text
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength || maxLength <= 3) {
    return text;
  }
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Gets the start and end timestamps for a local date string in YYYY-MM-DD format.
 * Returns the Unix timestamps (in milliseconds) for midnight at the start
 * and end of the given date in the server's local timezone.
 *
 * This is crucial for daily aggregations to ensure timestamps are correctly
 * aligned with the local calendar date, avoiding double-counting or missed
 * events when aggregating events across day boundaries.
 *
 * Uses date-fns-tz for DST-aware date range calculation. This correctly handles:
 * - Days with 23 hours (DST spring forward)
 * - Days with 25 hours (DST fall back)
 * - Timezone transitions across date boundaries
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Object with startOfDay and endOfDay timestamps
 */
export function getLocalDateRange(dateString: string): {
  startOfDay: number;
  endOfDay: number;
} {
  // Parse YYYY-MM-DD manually to avoid UTC interpretation issues
  const [year, month, day] = dateString.split("-").map(Number);
  const timeZone = getLocalTimeZone();

  // Create the date at midnight in the local timezone
  // Use formatInTimeZone to get the exact timestamp for local midnight
  const startOfDayStr = formatInTimeZone(
    new Date(year, month - 1, day),
    timeZone,
    "yyyy-MM-dd'T'00:00:00xxx"
  );
  const endOfDayStr = formatInTimeZone(
    new Date(year, month - 1, day),
    timeZone,
    "yyyy-MM-dd'T'23:59:59.999xxx"
  );

  return {
    startOfDay: toDate(startOfDayStr).getTime(),
    endOfDay: toDate(endOfDayStr).getTime() + 1,
  };
}

/**
 * Estimate timezone offset in minutes from UTC based on a local hour.
 * This is a simple inference based on the assumption that typical activity
 * patterns align with the person's local timezone.
 *
 * @param localHour - The hour of day in local time (0-23)
 * @returns Estimated timezone offset in minutes from UTC
 */
export function getTimezoneOffsetFromHour(localHour: number): number {
  const serverHour = new Date().getHours();
  const diff = localHour - serverHour;
  // Normalize to -720 to +720 range (valid timezone offsets)
  let offset = diff * 60;
  if (offset > 720) offset -= 1440;
  if (offset < -720) offset += 1440;
  return offset;
}
